import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '@/store/app';
import { useAuthStore } from '@/store/auth';
import { api, type Categoria } from '@/lib/api';

export default function RegistroModal() {
  const { modalRegistroOpen, nuevoCat, nuevoTitulo, nuevoEnviado, cerrarRegistro, setNuevoCat, setNuevoTitulo, markEnviado, resetRegistro } = useAppStore();
  const { token, user } = useAuthStore();
  const qc = useQueryClient();

  const { data: cats = [] } = useQuery<Categoria[]>({
    queryKey: ['categorias'],
    queryFn: () => api.categorias(token!),
    enabled: !!token && modalRegistroOpen,
  });

  const mutation = useMutation({
    mutationFn: () => api.crearReporte(token!, {
      titulo: nuevoTitulo,
      categoriaSlug: nuevoCat,
      sector: 'El Paraíso',
      brigadista: user?.nombre ?? 'Anónimo',
      prioridad: 'media',
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reportes'] });
      qc.invalidateQueries({ queryKey: ['pins'] });
      markEnviado();
    },
  });

  if (!modalRegistroOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) cerrarRegistro(); }}>
      <div style={{ width: '100%', maxWidth: 480, background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(24px)', borderRadius: '24px 24px 0 0', padding: 28, paddingBottom: 40, border: '1px solid rgba(255,255,255,0.1)', animation: 'floatIn 300ms ease-out' }}>
        {nuevoEnviado ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(34,197,94,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 24, color: '#22c55e' }}>
              <i className="ti ti-check" />
            </div>
            <h3 style={{ color: '#fff', fontWeight: 700, margin: '0 0 8px' }}>¡Reporte registrado!</h3>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, margin: '0 0 24px' }}>El hallazgo ha sido enviado al sistema.</p>
            <button onClick={resetRegistro} style={{ padding: '11px 32px', borderRadius: 12, background: '#1D4ED8', color: '#fff', fontWeight: 700, fontSize: 14 }}>
              Cerrar
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <h3 style={{ color: '#fff', fontWeight: 700, fontSize: 16, margin: 0 }}>Registrar hallazgo</h3>
              <button onClick={cerrarRegistro} style={{ color: 'rgba(255,255,255,0.4)', fontSize: 18 }}>
                <i className="ti ti-x" />
              </button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.5)', marginBottom: 8, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Categoría</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {cats.map((c) => (
                  <button key={c.slug} onClick={() => setNuevoCat(c.slug)}
                    style={{ padding: '7px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                      background: nuevoCat === c.slug ? c.color : 'rgba(255,255,255,0.07)',
                      color: nuevoCat === c.slug ? '#fff' : 'rgba(255,255,255,0.5)',
                      border: `1px solid ${nuevoCat === c.slug ? c.color : 'rgba(255,255,255,0.1)'}`,
                      display: 'flex', alignItems: 'center', gap: 5 }}>
                    <i className={`ti ${c.icon}`} style={{ fontSize: 12 }} />{c.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.5)', marginBottom: 8, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Descripción</label>
              <textarea value={nuevoTitulo} onChange={(e) => setNuevoTitulo(e.target.value)}
                placeholder="Describe brevemente el hallazgo…"
                rows={3}
                style={{ width: '100%', padding: '11px 14px', borderRadius: 12, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', fontSize: 13, resize: 'none', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            <button
              onClick={() => mutation.mutate()}
              disabled={!nuevoTitulo.trim() || mutation.isPending}
              style={{ width: '100%', padding: '13px', borderRadius: 14, background: !nuevoTitulo.trim() ? 'rgba(29,78,216,0.3)' : 'linear-gradient(135deg,#1D4ED8,#2563EB)', color: '#fff', fontWeight: 700, fontSize: 14, boxShadow: '0 8px 20px rgba(29,78,216,0.3)', cursor: !nuevoTitulo.trim() ? 'not-allowed' : 'pointer' }}
            >
              {mutation.isPending ? 'Enviando…' : 'Enviar reporte'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
